import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { UiAlert } from '@nightshift/ui-types';

import { CreateTicketDialog } from '../components/create-ticket-dialog.tsx';
import { Empty, Spinner } from '../components/primitives.tsx';
import { api, type SessionPresence, type SessionUser } from '../live/api.ts';
import { LiveProvider, useLive, useStore } from '../live/context.tsx';
import type { LiveStore } from '../live/store.ts';
import { relativeShort } from '../lib/format.ts';
import { retryPhrase } from '../lib/liveness.ts';
import {
  chime,
  planAlertBackgroundNotification,
  pruneBackgroundNotified,
  readBackgroundNotified,
  setFaviconBadge,
  setSoundEnabled,
  setTitleBadge,
  showBackgroundNotification,
  soundEnabled,
  writeBackgroundNotified,
} from '../lib/notify.ts';
import { isTypingTarget } from '../lib/keys.ts';
import { KEYBOARD_VIEWS } from '../lib/nav.ts';
import { useRoute, VIEW_LABEL, type ViewName } from '../lib/router.ts';
import { confirmDiscardChanges, installUnloadGuard } from '../lib/unsaved.ts';
import { BoardView } from '../views/board.tsx';
import { boardViewFrom, boardViewToRoute } from '../lib/board-view.ts';
import { ticketsFilterFrom, ticketsFilterToRoute } from '../lib/tickets-view.ts';
import { runsFilterFrom } from '../lib/runs.ts';
import { AgentsView } from '../views/agents.tsx';
import { BudgetsView } from '../views/budgets.tsx';
import { OpsView } from '../views/ops.tsx';
import { ReportsView } from '../views/reports.tsx';
import { RunsView } from '../views/runs.tsx';
import { HelpView } from '../views/help.tsx';
import { SettingsView } from '../views/settings.tsx';
import { TicketsView } from '../views/tickets.tsx';
import { ReviewQueueView } from '../views/review-queue.tsx';
import { TicketDrawer } from '../views/drawer/drawer.tsx';
import { Login } from './login.tsx';
import { NavRail } from './nav-rail.tsx';
import { Topbar } from './topbar.tsx';
import { MILESTONES_GRAPH_MODE } from '../views/graph/graph-view-switch.tsx';
import './shell.css';

const GraphView = lazy(() => import('../views/graph/graph.tsx'));
const MilestoneOrderView = lazy(() => import('../views/graph/milestone-order.tsx'));

export function App({ store }: { store: LiveStore }) {
  const [session, setSession] = useState<'unknown' | 'in' | 'out'>('unknown');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [presence, setPresence] = useState<SessionPresence[]>([]);

  useEffect(() => {
    void api
      .session()
      .then((state) => {
        setUser(state.user ?? null);
        setPresence(state.presence ?? []);
        setSession(state.authed ? 'in' : 'out');
      })
      .catch(() => setSession('out'));
  }, []);

  if (session === 'unknown') {
    return (
      <div className="shell">
        <nav className="nav type-fixed" aria-busy="true">
          <div className="nav-brand">
            <span className="nav-mark" aria-hidden="true" />
            <span>Nightshift</span>
          </div>
        </nav>
        <div className="main">
          <div className="topbar type-fixed">
            <span className="kicker">Signing in…</span>
          </div>
          <main className="view" id="main-view" style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
            <h1 className="visually-hidden">Signing in</h1>
            <Spinner size={20} />
          </main>
        </div>
      </div>
    );
  }

  if (session === 'out') {
    return (
      <Login
        onAuthed={(next) => {
          setUser(next);
          setSession('in');
        }}
      />
    );
  }

  return (
    <LiveProvider store={store}>
      <Shell
        user={user}
        presence={presence}
        onSignedOut={() => {
          setUser(null);
          setPresence([]);
          setSession('out');
        }}
      />
    </LiveProvider>
  );
}

function Shell({
  user,
  presence,
  onSignedOut,
}: {
  user: SessionUser | null;
  presence: SessionPresence[];
  onSignedOut: () => void;
}) {
  const store = useStore();
  const state = useLive();
  const { route, go, openTicket, closeTicket, replace } = useRoute();
  const [sound, setSound] = useState(soundEnabled);
  const [composing, setComposing] = useState(false);
  const alertsRef = useRef<Set<string>>(new Set());
  const notifiedRef = useRef<Set<string>>(readBackgroundNotified());

  useEffect(() => {
    if (route.project && route.project !== store.getState().projectSlug) {
      store.setProject(route.project);
    }
  }, [route.project, store]);

  useEffect(() => {
    const raw = route.milestone;
    const parsed = raw && /^\d+$/.test(raw) ? Number(raw) : null;
    if (parsed !== store.getState().milestoneId) store.setMilestone(parsed);
  }, [route.milestone, store]);

  useEffect(() => {
    const raw = route.phase;
    const parsed = raw && /^\d+$/.test(raw) ? Number(raw) : null;
    if (parsed !== store.getState().phaseId) store.setPhase(parsed);
  }, [route.phase, store]);

  /*
   * A snapshot echoes back the scope the server actually applied. A slug it does not know comes
   * back as null — the all-projects view — so a stale bookmark or a typed `/p/{slug}/` drops to
   * All projects and says so, instead of quietly showing every project's work under a name that
   * does not exist.
   */
  const reconciledScope = useRef<string | null>(null);
  const [unknownProject, setUnknownProject] = useState<string | null>(null);
  useEffect(() => {
    // Read the store, not this render's `state`: when the URL moves to /p/B the effect above
    // has just called setProject(B), and the rendered snapshot is still the all-projects one
    // from before the switch. Judging B by that snapshot calls a real project unknown. The
    // store only ever holds a snapshot fetched for its own current scope.
    const current = store.getState();
    if (!current.snapshot || current.snapshot.projectSlug !== null) return;
    const asked = current.projectSlug;
    // Once per bad slug: `replace` is a fresh function each render, so without this the effect
    // re-runs while the fallback is still settling and the operator gets the toast twice.
    if (!asked || reconciledScope.current === asked) return;
    reconciledScope.current = asked;
    store.setProject(null);
    if (route.project) replace({ project: null });
    setUnknownProject(asked);
    store.toast('info', `No project named ${asked} — showing all projects.`);
  }, [state.snapshot, route.project, replace, store]);

  useEffect(() => {
    store.start({
      onAlertRaised: (alert) => {
        // Only fire for genuinely new handovers, and never for the first snapshot: arriving
        // at a dashboard that already has three things waiting should not play three chimes.
        if (alertsRef.current.size === 0) return;
        void chime();
        store.toast('info', `${alert.ticketKey} needs you — ${alert.headline}`, {
          label: 'Open',
          run: () => resolveAlert(alert, go, openTicket),
        });
        const planned = planAlertBackgroundNotification(alert, notifiedRef.current);
        if (!planned) return;
        showBackgroundNotification(planned, () => resolveAlert(alert, go, openTicket));
        notifiedRef.current.add(alert.id);
        writeBackgroundNotified(notifiedRef.current);
      },
    });
    return () => store.stop();
  }, [store, go, openTicket]);

  const snapshot = state.snapshot;
  /*
   * One number, from the server, for every surface that answers "what needs me".
   *
   * The nav badge counted the whole inbox, the pill counted only high and critical without
   * saying so, and the board rail counted a third thing. Whatever the number is, it is the
   * same number in all of them now, and a surface that shows a subset labels the subset.
   */
  const waiting =
    snapshot?.attention.total ?? state.board?.summary.waitingNeedsAttention ?? 0;

  useEffect(() => {
    if (!snapshot) return;
    alertsRef.current = new Set(snapshot.alerts.map((alert) => alert.id));
    const pruned = pruneBackgroundNotified(notifiedRef.current, alertsRef.current);
    if (pruned.size !== notifiedRef.current.size) writeBackgroundNotified(pruned);
    notifiedRef.current = pruned;
  }, [snapshot]);

  useEffect(() => {
    setTitleBadge(waiting, VIEW_LABEL[route.view]);
    setFaviconBadge(waiting);
  }, [waiting, route.view]);

  // Session expiry is detected by the store (a 401 on refetch) rather than guessed from a
  // timer, so a cookie that outlived a server restart drops the operator at the login screen
  // instead of at a screen full of stale data.
  useEffect(() => {
    if (state.loadError === 'session-expired' || state.boardError === 'session-expired') onSignedOut();
  }, [state.loadError, state.boardError, onSignedOut]);

  const onAlertOpen = useCallback(
    (alert: UiAlert) => resolveAlert(alert, go, openTicket),
    [go, openTicket],
  );

  useKeyboard({
    onView: (view) => {
      go(view);
    },
    onEscape: () => (route.ticket ? closeTicket() : undefined),
  });

  // Reload and tab close ask about unsaved edits the same way in-app navigation does.
  useEffect(() => installUnloadGuard(), []);

  const selectProject = useCallback(
    (slug: string | null) => {
      // The router asks about unsaved edits when the project in the URL changes; asking here
      // as well would put the same question twice. The store only moves once the URL did.
      if (store.getState().projectSlug !== slug && !confirmDiscardChanges()) return;
      store.setProject(slug);
      replace({ project: slug, milestone: null, phase: null }, { confirmed: true });
    },
    [replace, store],
  );

  const selectPhase = useCallback(
    (id: number | null) => {
      // The store drops a milestone that is not in the new phase; the URL follows it.
      store.setPhase(id);
      const milestone = store.getState().milestoneId;
      replace({ phase: id == null ? null : String(id), milestone: milestone == null ? null : String(milestone) });
    },
    [replace, store],
  );

  const selectMilestone = useCallback(
    (id: number | null) => {
      store.setMilestone(id);
      replace({ milestone: id == null ? null : String(id) });
    },
    [replace, store],
  );

  const viewProps = useMemo(
    () => ({ onOpenTicket: openTicket, onOpenAlert: onAlertOpen }),
    [openTicket, onAlertOpen],
  );

  const showBudgets = (snapshot?.projects ?? []).some((project) =>
    project.capabilities.includes('budgets'),
  );

  const snapshotFailed =
    !snapshot && state.loadError !== null && state.loadError !== 'session-expired';
  const snapshotPending = !snapshot && !snapshotFailed;
  const boardView = route.view === 'board';

  return (
    <div className="shell">
      <a className="skip-link" href="#main-view">
        Skip to main view
      </a>
      <NavRail
        view={route.view}
        user={user}
        presence={presence}
        onGo={go}
        onSignOut={async () => {
          if (!confirmDiscardChanges()) return;
          await api.logout().catch(() => undefined);
          onSignedOut();
        }}
      />

      <div className="main">
        <Topbar
          showMilestone={route.view !== 'graph'}
          sound={sound}
          onSound={(next) => {
            setSoundEnabled(next);
            setSound(next);
            if (next) void chime();
          }}
          onSelectProject={selectProject}
          onSelectPhase={selectPhase}
          onSelectMilestone={selectMilestone}
          onCreateTicket={() => setComposing(true)}
          onOpenTicket={openTicket}
          onOpenView={go}
        />

        <Banners
          unknownProject={unknownProject}
          onDismissUnknown={() => setUnknownProject(null)}
        />

        <main className="view" id="main-view" tabIndex={-1}>
          {route.view !== 'reports' && route.view !== 'budgets' ? (
            <h1 className="visually-hidden">{VIEW_LABEL[route.view]}</h1>
          ) : null}
          {boardView ? (
            <BoardView
              {...viewProps}
              user={user}
              view={boardViewFrom(route)}
              onChangeView={(next) => replace(boardViewToRoute(next))}
              onOpenTicketsIndex={(filter) =>
                go('tickets', { state: filter.state, reason: filter.reason ?? null })
              }
            />
          ) : route.view === 'tickets' ? (
            <TicketsView
              {...viewProps}
              filter={ticketsFilterFrom(route)}
              onChangeFilter={(next) => replace(ticketsFilterToRoute(next))}
            />
          ) : route.view === 'review' ? (
            <ReviewQueueView {...viewProps} />
          ) : route.view === 'graph' ? (
            <Suspense
              fallback={
                <div className="graph-route-loading" aria-label="Loading Graph">
                  <Spinner size={20} />
                </div>
              }
            >
              {route.graphMode === MILESTONES_GRAPH_MODE ? (
                <MilestoneOrderView
                  projectSlug={state.projectSlug}
                  userId={user?.id ?? null}
                  refreshKey={state.snapshot?.revision ?? 0}
                  onChangeView={(view) => replace({ graphMode: view === 'milestones' ? MILESTONES_GRAPH_MODE : null })}
                  onShowTickets={(milestoneId) => replace({ graphMode: null, milestone: String(milestoneId) })}
                  onOpenTicket={openTicket}
                />
              ) : (
              <GraphView
                projectSlug={state.projectSlug}
                userId={user?.id ?? null}
                connection={state.connection}
                refreshKey={state.snapshot?.revision ?? 0}
                drawerOpen={route.ticket !== null}
                onOpenTicket={openTicket}
                onCreateTicket={() => setComposing(true)}
                urlState={{
                  preset: route.graphPreset,
                  state: route.state,
                  query: route.q,
                  milestone: route.milestone,
                  category: route.graphCategory,
                  mode: route.graphMode,
                }}
                onChangeUrlState={(next) => replace({
                  graphPreset: next.preset,
                  state: next.state,
                  q: next.query,
                  milestone: next.milestone,
                  graphCategory: next.category,
                  graphMode: next.mode,
                })}
              />
              )}
            </Suspense>
          ) : route.view === 'agents' ? (
            <AgentsView user={user} />
          ) : route.view === 'reports' ? (
            <ReportsView onGo={go} showBudgets={showBudgets} projectSlug={state.projectSlug} />
          ) : snapshotFailed ? (
            <Empty
              title="Nightshift could not be read."
              body={`The orchestrator answered, but not with anything usable: ${state.loadError}. Nothing here is stale — it was never loaded.`}
              action={
                <button type="button" className="btn btn-accent" onClick={() => void store.refetch()}>
                  Try again
                </button>
              }
            />
          ) : snapshotPending ? (
            <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
              <Spinner size={20} />
            </div>
          ) : route.view === 'budgets' ? (
            <BudgetsView
              {...viewProps}
              selectedId={route.budget}
              onSelectId={(id) => replace({ budget: id, group: id === route.budget ? route.group : null })}
              selectedProbe={route.group}
              onSelectProbe={(probe) => replace({ group: probe })}
            />
          ) : route.view === 'ops' ? (
            <OpsView {...viewProps} />
          ) : route.view === 'runs' ? (
            <RunsView
              {...viewProps}
              filter={runsFilterFrom({
                ticket: route.ticket,
                state: route.state,
                status: route.status,
                harness: route.harness,
                outcome: route.outcome,
              })}
              onChangeFilter={(next) =>
                replace({
                  ticket: next.ticket,
                  state: next.state,
                  status: next.status,
                  harness: next.harness,
                  outcome: next.outcome,
                })
              }
              selectedRun={route.run}
              selectedRunLane={route.runLane ?? 'attempt'}
              onSelectRun={(run, runLane = 'attempt') => replace({ run, runLane })}
            />
          ) : route.view === 'help' ? (
            <HelpView />
          ) : (
            <SettingsView
              selectedGroup={route.group}
              onSelectGroup={(group) => replace({ group })}
              user={user}
            />
          )}
        </main>
      </div>

      {route.ticket ? (
        <TicketDrawer
          // Keyed so a different ticket gets a fresh drawer: a half-typed note, an open
          // confirm or a draft title belongs to the ticket it was started on.
          key={route.ticket}
          ticketKey={route.ticket}
          initialRun={route.run}
          selectedTab={route.drawer}
          onSelectTab={(tab) => replace({ drawer: tab })}
          onClose={closeTicket}
          user={user}
        />
      ) : null}

      {composing ? (
        <CreateTicketDialog
          defaultProject={state.projectSlug}
          onClose={() => setComposing(false)}
          onCreated={(key) => openTicket(key)}
        />
      ) : null}

      <Toasts />
    </div>
  );
}

/* ── banners ───────────────────────────────────────────────────────────────── */

function Banners({
  unknownProject,
  onDismissUnknown,
}: {
  unknownProject: string | null;
  onDismissUnknown: () => void;
}) {
  const store = useStore();
  const state = useLive();
  const snapshot = state.snapshot;
  const [adaptersDismissed, setAdaptersDismissed] = useState(false);
  const [runnersDismissed, setRunnersDismissed] = useState(false);
  // `live` is computed once on the server; never recompute it here.
  const liveRunners = snapshot?.runners.filter((runner) => runner.live).length ?? 0;

  return (
    <>
      {unknownProject ? (
        <div className="banner banner-warn" role="status">
          <strong style={{ fontWeight: 500 }}>
            Project &lsquo;{unknownProject}&rsquo; not found — showing all projects.
          </strong>
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={onDismissUnknown}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {state.connection === 'offline' ? (
        <div className="banner banner-bad">
          <span className="dot" style={{ background: 'var(--danger)', width: 8, height: 8 }} />
          <strong style={{ fontWeight: 500 }}>
            Event stream disconnected
            {retryPhrase(state.reconnectInSeconds)}.
          </strong>
          <span className="dim">
            Last confirmed {relativeShort(state.lastContactAt, state.tick)} ago. Everything below is
            that old; elapsed timers are still counting but nothing new has arrived.
          </span>
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => store.reconnectNow()}
          >
            Retry now
          </button>
        </div>
      ) : null}

      {snapshot?.dispatchPaused ? (
        <div className="banner banner-warn">
          <span className="dot" style={{ background: 'var(--warn)', width: 8, height: 8 }} />
          <strong style={{ fontWeight: 500 }}>Dispatch is paused.</strong>
          <span className="dim">
            Queued jobs stay queued and no runner may claim one. Agents already running are
            finishing normally.
          </span>
        </div>
      ) : null}

      {/*
        One runner is not a fleet.

        Every outage that took the factory down for hours was a single machine: ai-rig silently
        exited and nothing was running for a day; mediaservervm's scheduled task reported Ready
        with no process behind it. With two machines online either failure costs throughput and
        nothing else. This says so while it is cheap to fix, rather than after a ticket has sat
        unclaimed for ten minutes and the stall detector has had to explain it.

        Dismissible for the session: it is a standing condition, not an event.
      */}
      {snapshot && liveRunners < 2 && !runnersDismissed ? (
        <div className="banner banner-warn">
          <span className="dot" style={{ background: 'var(--warn)', width: 8, height: 8 }} />
          <strong style={{ fontWeight: 500 }}>
            {liveRunners === 0
              ? 'No runner is online.'
              : 'Only one runner is online.'}
          </strong>
          <span className="dim">
            {liveRunners === 0
              ? 'Nothing can be claimed. Queued work waits until a runner comes back.'
              : 'Everything queued depends on one machine; if it stops, so does the factory.'}
          </span>
          <a className="btn btn-sm" style={{ marginLeft: 'auto' }} href="/settings?group=machines">
            Machines
          </a>
          <button
            type="button"
            className="banner-dismiss"
            onClick={() => setRunnersDismissed(true)}
            title="Hide until the next reload."
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/*
        Dismissible, unlike the two above it.

        An unconfigured adapter is a standing condition, not an event: it was true when the
        operator logged in, it will be true tomorrow, and it is also sitting in Attention as an
        item with an "Open settings" button. Repeating it as a permanent band across the top of
        every screen taxes every visit for a fact the operator has already read and cannot
        action from here. Dismissing it lasts for the session — a reload brings it back, because
        the condition has not gone away and a warning that can be silenced forever is not a
        warning.
      */}
      {snapshot && !snapshot.health.pipelineActive && !adaptersDismissed ? (
        <div className="banner banner-warn">
          <span className="dot" style={{ background: 'var(--warn)', width: 8, height: 8 }} />
          <strong style={{ fontWeight: 500 }}>Tracker and git-host adapters are not configured.</strong>
          <span className="dim">
            Everything recorded locally still reads correctly, but conversations cannot be
            fetched and no action that writes to the tracker or GitLab will work.
          </span>
          <button
            type="button"
            className="banner-dismiss"
            onClick={() => setAdaptersDismissed(true)}
            title="Hide until the next reload."
          >
            Dismiss
          </button>
        </div>
      ) : null}
    </>
  );
}

/* ── toasts ────────────────────────────────────────────────────────────────── */

function Toasts() {
  const store = useStore();
  const state = useLive();
  if (state.toasts.length === 0) return null;
  return (
    <div className="toast-stack" role="status" aria-live="polite">
      {state.toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.tone}`}>
          <span
            className="dot"
            style={{
              marginTop: 5,
              background:
                toast.tone === 'good'
                  ? 'var(--good)'
                  : toast.tone === 'bad'
                    ? 'var(--danger)'
                    : 'var(--accent)',
            }}
          />
          <span style={{ flex: 1 }}>{toast.message}</span>
          {toast.action ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                void toast.action?.run();
                store.dismissToast(toast.id);
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => store.dismissToast(toast.id)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── keyboard ──────────────────────────────────────────────────────────────── */

/**
 * Keyboard shortcuts.
 *
 * Surviving destinations: `b`/`1` Board, `t` Tickets, `a` Agents, `r` Reports,
 * `s` Settings, `2` Ops, `3` Runs, `g` Budgets.
 */
function useKeyboard(handlers: {
  onView: (view: ViewName, key: string) => void;
  onEscape: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      if (event.key === 'Escape') {
        // An overlay above the drawer (search, the write-ticket dialog, an evidence zoom)
        // claims its own Escape. One press closes one thing.
        if (event.defaultPrevented) return;
        // Escape inside a field cancels that field; a second press then closes the drawer.
        if (typing) return;
        handlers.onEscape();
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      const mapped = KEYBOARD_VIEWS[event.key.toLowerCase()] ?? KEYBOARD_VIEWS[event.key];
      if (!mapped) return;
      event.preventDefault();
      handlers.onView(mapped, event.key.toLowerCase());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers]);
}

function resolveAlert(
  alert: UiAlert,
  go: (view: ViewName) => void,
  openTicket: (key: string, run?: number) => void,
): void {
  if (alert.action === 'open-setup') {
    go('settings');
    return;
  }
  // Operator alerts are about a machine or the backups, not a ticket.
  if (alert.action === 'open-machines') {
    go('agents');
    return;
  }
  if (alert.action === 'open-ops') {
    go('ops');
    return;
  }
  openTicket(alert.ticketKey, alert.runId);
}
